import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FileDown } from "lucide-react";

import { AdminCard, FilterChip, ListState } from "@/components/admin/primitives";
import { CommissionTable, CommissionTiles } from "@/components/comissoes/CommissionTable";
import { formatDate } from "@/lib/dates";
import { formatBRL } from "@/lib/format";
import {
  COMMISSION_HOLD_DAYS,
  useResellerCommissions,
  type CommissionStatus,
} from "@/lib/resellers";
import { useStoreSettings } from "@/lib/settings";

export const Route = createFileRoute("/revendedor/_area/comissoes")({
  head: () => ({ meta: [{ title: "Comissões — Revenda MonteBox" }] }),
  component: ResellerCommissions,
});

type Filter = "todas" | CommissionStatus;

const FILTERS: [Filter, string][] = [
  ["todas", "Todas"],
  ["GERADA", "A liberar"],
  ["LIBERADA", "Liberadas"],
  ["PAGA", "Pagas"],
  ["CANCELADA", "Canceladas"],
];

/**
 * Extrato do revendedor: gerada → liberada → paga, e os repasses.
 *
 * Só números dele — valor da venda e comissão. Custo e lucro da empresa
 * nunca chegam aqui.
 */
function ResellerCommissions() {
  const statement = useResellerCommissions();
  const [filter, setFilter] = useState<Filter>("todas");
  const data = statement.data;
  const settings = useStoreSettings().data;
  const locked = settings?.payoutsLocked ?? false;
  const extraDays = settings?.payment.payoutExtraDays;
  const entries = (data?.entries ?? []).filter(
    (entry) => filter === "todas" || entry.status === filter,
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-mb-navy">
          Comissões e repasses
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-mb-muted">
          A comissão nasce quando a venda é paga e é liberada {COMMISSION_HOLD_DAYS} dias depois de
          o pedido ser concluído (prazo para troca ou devolução). As liberadas são repassadas
          automaticamente pelo Mercado Pago para a conta conectada em Meus dados
          {extraDays === undefined
            ? "."
            : `, ${extraDays} ${extraDays === 1 ? "dia" : "dias"} depois de o Mercado Pago liberar o dinheiro da venda.`}
        </p>
      </div>

      {data && (
        <CommissionTiles totals={data.totals} holdDays={COMMISSION_HOLD_DAYS} locked={locked} />
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([value, label]) => (
          <FilterChip key={value} active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterChip>
        ))}
      </div>

      <AdminCard className="overflow-hidden">
        <ListState
          loading={statement.isLoading}
          error={statement.error}
          empty={entries.length === 0}
          emptyLabel="Nenhuma comissão por aqui ainda."
        />
        {entries.length > 0 && <CommissionTable entries={entries} />}
      </AdminCard>

      {locked && (
        <p className="rounded-mb-field border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          Os repasses estão pausados pela MonteBox no momento. Suas comissões continuam sendo
          liberadas e são repassadas assim que voltarem.
        </p>
      )}

      <AdminCard className="p-6">
        <h2 className="font-display text-base font-bold text-mb-navy">Repasses recebidos</h2>
        {(data?.payouts ?? []).length === 0 ? (
          <p className="py-6 text-center text-sm text-mb-muted">Nenhum repasse ainda.</p>
        ) : (
          <ul className="mt-3 flex flex-col">
            {data!.payouts.map((payout) => (
              <li
                key={payout.id}
                className="flex items-center justify-between gap-3 border-t border-mb-line py-3 text-sm first:border-t-0"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-mb-ink">{formatDate(payout.paidAt)}</p>
                  <p className="text-xs text-mb-muted">
                    {payout.entries} lançamento{payout.entries === 1 ? "" : "s"}
                    {payout.note && ` · ${payout.note}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  {payout.receiptUrl && (
                    <a
                      href={payout.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs font-bold text-mb-navy hover:underline"
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      Comprovante
                    </a>
                  )}
                  <span className="font-display font-bold tabular-nums text-mb-navy">
                    {formatBRL(payout.amount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AdminCard>
    </div>
  );
}
